import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Shield, Server, Monitor, Globe, Lock, Database, AlertTriangle, FileText, Building2, Phone } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';

function DataFlowRow({ from, to, data, via, personal, storage }: {
  from: string;
  to: string;
  data: string;
  via: string;
  personal: boolean;
  storage?: string;
}) {
  return (
    <tr className="border-t">
      <td className="p-2 text-xs">{from}</td>
      <td className="p-2 text-xs">{to}</td>
      <td className="p-2 text-xs">{data}</td>
      <td className="p-2 text-xs">{via}</td>
      <td className="p-2 text-xs text-center">
        {personal ? (
          <Badge variant="destructive" className="text-[10px]">Ja</Badge>
        ) : (
          <Badge variant="secondary" className="text-[10px]">Nee</Badge>
        )}
      </td>
      {storage !== undefined && <td className="p-2 text-xs">{storage}</td>}
    </tr>
  );
}

export default function DocumentationPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Terug
        </Button>
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Documentatie — Privacy, Datastromen & AVG
        </h1>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">

        {/* Introductie */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              AVG / GDPR — Overzicht dataverwerking
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Dit document beschrijft <strong>welke data waarheen stroomt</strong> bij het gebruik van MicroHIS VTS,
              welke <strong>platforms en partijen</strong> betrokken zijn, en welke <strong>verwerkersovereenkomsten</strong> nodig zijn.
            </p>
            <p>
              Alle AI-functionaliteit (SOEP-generatie, vertaling, ICPC-suggesties, TTS) loopt <strong>rechtstreeks vanuit de browser</strong> naar
              Microsoft Azure diensten. Er gaat <strong>geen medische data</strong> via Lovable Cloud.
            </p>
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3 flex items-start gap-2">
              <Shield className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
              <p className="text-xs text-green-800 dark:text-green-300">
                <strong>Privacy by design:</strong> Transcripten, audio en consultteksten worden <strong>nooit</strong> via
                Lovable Cloud verstuurd. Alle AI-verwerking verloopt rechtstreeks naar de eigen Azure-tenant van de praktijk.
              </p>
            </div>
            <div className="bg-muted rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs">
                <strong>Belangrijk:</strong> Patiëntgegevens (naam, BSN, geboortedatum) worden <strong>niet</strong> door
                de applicatie verstuurd naar AI-diensten. Het transcript bevat uitsluitend het gesprokene tijdens het consult.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Betrokken platforms */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Betrokken platforms & partijen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-2 font-medium">Platform / Partij</th>
                    <th className="text-left p-2 font-medium">Eigenaar</th>
                    <th className="text-left p-2 font-medium">Rol</th>
                    <th className="text-left p-2 font-medium">Locatie data</th>
                    <th className="text-left p-2 font-medium">Verwerkt persoonsgegevens?</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t">
                    <td className="p-2 font-medium">Lovable Cloud</td>
                    <td className="p-2">Lovable (Stockholm, Zweden)</td>
                    <td className="p-2">Hosting web-app, database, authenticatietokens, ICPC-validatie, e-mail</td>
                    <td className="p-2">EU (Supabase infrastructure)</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Beperkt</Badge> — geen medische data</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">Microsoft Azure — Speech Services</td>
                    <td className="p-2">Microsoft</td>
                    <td className="p-2">Spraakherkenning (audio → tekst) + Text-to-Speech</td>
                    <td className="p-2">Configureerbaar per regio</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Ja</Badge> — live audio consult</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">Microsoft Azure — AI Foundry</td>
                    <td className="p-2">Klant (eigen Azure-tenant)</td>
                    <td className="p-2">SOEP-generatie, vertaling, ICPC-suggesties</td>
                    <td className="p-2">Eigen Azure-tenant</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Ja</Badge> — transcript consult</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">Microsoft Graph</td>
                    <td className="p-2">Microsoft</td>
                    <td className="p-2">E-mail verificatie (onboarding)</td>
                    <td className="p-2">Microsoft 365 tenant</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Beperkt</Badge> — alleen e-mailadres</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">MicroHIS (lokaal)</td>
                    <td className="p-2">Klant</td>
                    <td className="p-2">EPD — ontvangt SOEP-verslag</td>
                    <td className="p-2">Lokaal werkstation</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Lokaal</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">SIP-telefooncentrale (FreePBX/Asterisk)</td>
                    <td className="p-2">Klant (eigen PBX) of telecom-leverancier</td>
                    <td className="p-2">Telefonie — WebRTC softphone (WSS-signalling + SRTP-audio)</td>
                    <td className="p-2">Eigen praktijk / telecom-omgeving</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Ja</Badge> — live spraak telefoongesprek</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Architectuur overzicht */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Architectuur — Componenten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="border rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-2 font-medium">
                  <Monitor className="h-4 w-4 text-blue-500" />
                  Client (Browser)
                </div>
                <p className="text-xs text-muted-foreground">
                  De web-app draait in de browser (of WebView2 in MicroHIS).
                  Audio, SOEP-generatie, vertaling, ICPC-suggesties en TTS gaan <strong>allemaal direct</strong> naar Azure — zonder tussenkomst van Lovable Cloud.
                </p>
              </div>
              <div className="border rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-2 font-medium">
                  <Server className="h-4 w-4 text-green-500" />
                  Lovable Cloud (Edge Functions)
                </div>
                <p className="text-xs text-muted-foreground">
                  Serverless functies voor: authenticatie-tokens (Azure Speech), ICPC-code validatie tegen database, e-mailverificatie en gebruiksregistratie.
                  <strong> Geen medische data passeert deze laag.</strong>
                </p>
              </div>
              <div className="border rounded-lg p-3 space-y-1">
                <div className="flex items-center gap-2 font-medium">
                  <Database className="h-4 w-4 text-purple-500" />
                  Lovable Cloud Database
                </div>
                <p className="text-xs text-muted-foreground">
                  Bevat: praktijkconfiguratie, gebruikersinstellingen, ICPC-codes, gebruiksstatistieken.
                  Bevat <strong>geen</strong> transcripten, audio of SOEP-verslagen.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gedetailleerde datastromen */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Gedetailleerde datastromen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" defaultValue={["speech", "soep", "tts", "translate", "icpc", "email", "sip", "other"]} className="w-full">

              {/* 1. Spraakherkenning */}
              <AccordionItem value="speech">
                <AccordionTrigger className="text-sm font-medium">
                  🎙️ Spraakherkenning (Speech-to-Text)
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                    <p><strong>Route:</strong> Browser → <span className="text-green-600 font-medium">DIRECT</span> → Azure Speech Services</p>
                    <p><strong>Lovable Cloud betrokkenheid:</strong> Alleen voor het ophalen van een kortstondig authenticatie-token (geen patiëntdata).</p>
                    <p><strong>Opslag:</strong> Geen. Audio wordt real-time verwerkt en niet bewaard.</p>
                  </div>
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-muted"><th className="text-left p-2">Stap</th><th className="text-left p-2">Van</th><th className="text-left p-2">Naar</th><th className="text-left p-2">Data</th></tr></thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-t">
                          <td className="p-2 font-medium">1. Token ophalen</td>
                          <td className="p-2">Browser</td>
                          <td className="p-2">Edge Function → Azure STS</td>
                          <td className="p-2">Geen patiëntdata. Alleen authenticatieverzoek met server-side API key.</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-medium">2. Audio streaming</td>
                          <td className="p-2">Browser (microfoon)</td>
                          <td className="p-2">Azure Speech Services <strong>(direct)</strong></td>
                          <td className="p-2">
                            <Badge variant="destructive" className="text-[10px] mr-1">Persoonsgegevens</Badge>
                            Live audio van het consult. Gaat <strong>niet</strong> via Lovable Cloud.
                          </td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-medium">3. Transcriptie retour</td>
                          <td className="p-2">Azure Speech Services</td>
                          <td className="p-2">Browser <strong>(direct)</strong></td>
                          <td className="p-2">Getranscribeerde tekst met sprekerherkenning. Gaat <strong>niet</strong> via Lovable Cloud.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                    <p className="text-xs text-green-800 dark:text-green-300">
                      ✅ <strong>Audio wordt NIET via Lovable Cloud verstuurd.</strong> De browser maakt via de Speech SDK een
                      directe WebSocket-verbinding met Azure Cognitive Services. Lovable Cloud levert enkel het authenticatietoken.
                    </p>
                  </div>
                  <pre className="bg-muted p-3 rounded-md text-[10px] overflow-x-auto whitespace-pre font-mono">
{`┌──────────────┐  1. Token request   ┌───────────────────┐  AZURE_SPEECH_KEY  ┌─────────────────┐
│   Browser    │ ──────────────────► │  Edge Function    │ ─────────────────► │  Azure STS      │
│              │ ◄────────────────── │  (azure-speech-   │ ◄───────────────── │  (issueToken)   │
│              │  { token, region }  │   token)          │  short-lived token │                 │
└──────┬───────┘                     └───────────────────┘                    └─────────────────┘
       │
       │ 2. WebSocket (directe verbinding met token)
       │    Audio stream ↑↓ Transcriptie
       ▼
┌──────────────────────────────┐
│  Azure Speech Services       │
│  (Conversation Transcriber)  │
└──────────────────────────────┘`}
                  </pre>
                </AccordionContent>
              </AccordionItem>

              {/* 2. SOEP-generatie */}
              <AccordionItem value="soep">
                <AccordionTrigger className="text-sm font-medium">
                  📝 SOEP-generatie (Transcript → Verslag)
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                    <p><strong>Route:</strong> Browser → <span className="text-green-600 font-medium">DIRECT</span> → Azure AI Foundry (eigen tenant)</p>
                    <p><strong>Lovable Cloud betrokkenheid:</strong> <span className="text-green-600 font-medium">Geen — transcript gaat rechtstreeks naar Azure AI Foundry vanuit de browser.</span></p>
                    <p><strong>Opslag:</strong> Geen. Transcript wordt niet opgeslagen, alleen in-memory verwerkt.</p>
                  </div>

                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-muted"><th className="text-left p-2">Stap</th><th className="text-left p-2">Van</th><th className="text-left p-2">Naar</th><th className="text-left p-2">Data</th></tr></thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-t">
                          <td className="p-2 font-medium">1. Transcript versturen</td>
                          <td className="p-2">Browser</td>
                          <td className="p-2">Azure AI Foundry <strong>(direct)</strong></td>
                          <td className="p-2">
                            <Badge variant="destructive" className="text-[10px] mr-1">Persoonsgegevens</Badge>
                            Systeemprompt + transcript. Endpoint + API key worden client-side gebruikt.
                          </td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-medium">2. SOEP retour</td>
                          <td className="p-2">Azure AI Foundry</td>
                          <td className="p-2">Browser <strong>(direct)</strong></td>
                          <td className="p-2">SOEP JSON retour. Gaat niet via Lovable Cloud.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                    <p className="text-xs text-green-800 dark:text-green-300">
                      ✅ <strong>Het transcript gaat NIET via Lovable Cloud.</strong> De browser roept Azure AI Foundry
                      rechtstreeks aan. De Azure API key en endpoint zijn client-side beschikbaar. Het transcript
                      verlaat het werkstation alleen richting uw eigen Azure-tenant.
                    </p>
                  </div>

                  <pre className="bg-muted p-3 rounded-md text-[10px] overflow-x-auto whitespace-pre font-mono">
{`┌──────────────┐  transcript + prompt  ┌──────────────────┐
│   Browser    │ ────────────────────► │  Azure AI Foundry│
│              │ ◄──────────────────── │  (eigen tenant)  │
│              │    SOEP JSON          │                  │
└──────────────┘                       └──────────────────┘
  Lovable Cloud wordt NIET gebruikt voor deze stroom.`}
                  </pre>
                </AccordionContent>
              </AccordionItem>

              {/* 3. Text-to-Speech */}
              <AccordionItem value="tts">
                <AccordionTrigger className="text-sm font-medium">
                  🔊 Text-to-Speech (Voorlezen)
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                    <p><strong>Route:</strong> Browser → <span className="text-green-600 font-medium">DIRECT</span> → Azure Speech Services (TTS)</p>
                    <p><strong>Lovable Cloud betrokkenheid:</strong> Alleen voor het ophalen van een kortstondig authenticatie-token (geen tekst of audio).</p>
                    <p><strong>Opslag:</strong> Geen. Tekst en audio worden niet opgeslagen.</p>
                  </div>
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-muted"><th className="text-left p-2">Stap</th><th className="text-left p-2">Van</th><th className="text-left p-2">Naar</th><th className="text-left p-2">Data</th></tr></thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-t">
                          <td className="p-2 font-medium">1. Token ophalen</td>
                          <td className="p-2">Browser</td>
                          <td className="p-2">Edge Function → Azure STS</td>
                          <td className="p-2">Geen patiëntdata. Alleen authenticatieverzoek (hergebruik van spraakherkenningstoken).</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-medium">2. Synthese</td>
                          <td className="p-2">Browser (Speech SDK)</td>
                          <td className="p-2">Azure Speech Services <strong>(direct)</strong></td>
                          <td className="p-2">
                            <Badge variant="destructive" className="text-[10px] mr-1">Mogelijk persoonsgegevens</Badge>
                            Tekst om voor te lezen. Gaat <strong>niet</strong> via Lovable Cloud.
                          </td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-medium">3. Audio retour</td>
                          <td className="p-2">Azure Speech Services</td>
                          <td className="p-2">Browser <strong>(direct)</strong></td>
                          <td className="p-2">MP3 audiobestand. Gaat <strong>niet</strong> via Lovable Cloud.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                    <p className="text-xs text-green-800 dark:text-green-300">
                      ✅ <strong>Tekst en audio worden NIET via Lovable Cloud verstuurd.</strong> De browser gebruikt de Azure Speech SDK
                      om rechtstreeks met Azure Speech Services te communiceren. Lovable Cloud levert enkel het authenticatietoken
                      (hetzelfde token als voor spraakherkenning).
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 4. Vertaling */}
              <AccordionItem value="translate">
                <AccordionTrigger className="text-sm font-medium">
                  🌐 Bi-directionele vertaling
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                    <p><strong>Route:</strong> Browser → <span className="text-green-600 font-medium">DIRECT</span> → Azure AI Foundry (eigen tenant)</p>
                    <p><strong>Lovable Cloud betrokkenheid:</strong> <span className="text-green-600 font-medium">Geen — vertaling gaat rechtstreeks naar Azure AI Foundry vanuit de browser.</span></p>
                    <p><strong>Opslag:</strong> Geen.</p>
                    <p><strong>Richting:</strong> Bi-directioneel — zorgverlener → patiënttaal en patiënt → zorgverlenertaal.</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                    <p className="text-xs text-green-800 dark:text-green-300">
                      ✅ <strong>Vertaling gaat NIET via Lovable Cloud.</strong> Wanneer de vertaal-trigger wordt geactiveerd 
                      (via commandowoord, Vertaal-knop of sneltoets), worden ongelezen berichten bi-directioneel vertaald: 
                      berichten van de zorgverlener naar de taal van de patiënt, en berichten van de patiënt naar de taal van de zorgverlener.
                      Alle vertaling verloopt <strong>rechtstreeks vanuit de browser</strong> naar Azure AI Foundry.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 5. ICPC-suggesties */}
              <AccordionItem value="icpc">
                <AccordionTrigger className="text-sm font-medium">
                  🏥 ICPC-code suggesties
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                    <p><strong>Route:</strong> Browser → <span className="text-green-600 font-medium">DIRECT</span> → Azure AI Foundry (evaluatietekst) + Browser → Edge Function (alleen ICPC-codes valideren, géén persoonsgegevens)</p>
                    <p><strong>Lovable Cloud betrokkenheid:</strong> <span className="text-green-600 font-medium">Minimaal — alleen ICPC-codes (geen evaluatietekst) passeren de edge function voor databasevalidatie.</span></p>
                    <p><strong>Opslag:</strong> Geen. Alleen de ICPC-codetabel wordt geraadpleegd.</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                    <p className="text-xs text-green-800 dark:text-green-300">
                      ✅ <strong>De evaluatietekst gaat NIET via Lovable Cloud.</strong> ICPC-suggesties worden rechtstreeks vanuit de browser
                      naar Azure AI Foundry gestuurd. Alleen de gesuggereerde codes (bijv. "K86", "R74") worden via een edge function
                      gevalideerd tegen de ICPC-database. Dit betreft geen persoonsgegevens.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 6. E-mail verificatie */}
              <AccordionItem value="email">
                <AccordionTrigger className="text-sm font-medium">
                  ✉️ E-mail verificatie (Onboarding)
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                    <p><strong>Route:</strong> Browser → Lovable Cloud Edge Function → Microsoft Graph API</p>
                    <p><strong>Lovable Cloud betrokkenheid:</strong> <span className="text-amber-600 font-medium">Volledig — e-mailadres en verificatiecode passeren de edge function.</span></p>
                    <p><strong>Opslag:</strong> E-mailadres wordt opgeslagen in de database. Verificatiecode is tijdelijk (verloopt na 10 min).</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">
                      Bij de onboarding-wizard wordt een verificatie-e-mail verstuurd via Microsoft Graph (eigen M365-tenant).
                      De verificatiecode wordt tijdelijk opgeslagen in de database en na verificatie verwijderd.
                      Het e-mailadres van de gebruiker wordt permanent opgeslagen voor contactdoeleinden.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 7. Overige stromen */}
              <AccordionItem value="sip">
                <AccordionTrigger className="text-sm font-medium">
                  📞 Telefonie (SIP / WebRTC softphone)
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-xs">
                    <p><strong>Route:</strong> Browser → <span className="text-green-600 font-medium">DIRECT</span> → SIP-telefooncentrale van de praktijk (FreePBX/Asterisk via WSS + SRTP)</p>
                    <p><strong>Lovable Cloud betrokkenheid:</strong> Geen — Lovable Cloud verzorgt uitsluitend opslag van de SIP-configuratie (host, extensie, wachtwoord) en is niet betrokken bij signalering of audio.</p>
                    <p><strong>Microsoft Azure betrokkenheid:</strong> Geen — er gaat geen gespreksaudio richting Azure.</p>
                    <p><strong>Opslag:</strong> Geen opnames of metadata van gesprekken; logging van calls valt onder de eigen PBX.</p>
                  </div>
                  <div className="border rounded-md overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-muted"><th className="text-left p-2">Stap</th><th className="text-left p-2">Van</th><th className="text-left p-2">Naar</th><th className="text-left p-2">Data</th></tr></thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-t">
                          <td className="p-2 font-medium">1. SIP-registratie</td>
                          <td className="p-2">Browser (JsSIP)</td>
                          <td className="p-2">PBX via <code>wss://&lt;host&gt;/ws</code> <strong>(direct)</strong></td>
                          <td className="p-2">Extensie + wachtwoord (SIP digest auth). Geen patiëntdata.</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-medium">2. Signalering (INVITE/BYE)</td>
                          <td className="p-2">Browser</td>
                          <td className="p-2">PBX <strong>(direct)</strong></td>
                          <td className="p-2">Gekozen telefoonnummer (eventueel met beluitprefix). Geen patiëntinhoud.</td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-medium">3. Mediastream (audio)</td>
                          <td className="p-2">Browser (microfoon)</td>
                          <td className="p-2">PBX / tegenpartij via WebRTC (SRTP) <strong>(direct)</strong></td>
                          <td className="p-2">
                            <Badge variant="destructive" className="text-[10px] mr-1">Persoonsgegevens</Badge>
                            Live gesprekaudio. Gaat <strong>niet</strong> via Lovable Cloud of Azure.
                          </td>
                        </tr>
                        <tr className="border-t">
                          <td className="p-2 font-medium">4. STUN / TURN (NAT)</td>
                          <td className="p-2">Browser</td>
                          <td className="p-2">STUN-server (publiek) / TURN-relay van de praktijk</td>
                          <td className="p-2">ICE-kandidaten (IP-adres + poort) — geen gespreksaudio bij STUN; bij TURN wordt audio gerelayed door de eigen TURN-server.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                    <p className="text-xs text-green-800 dark:text-green-300">
                      ✅ <strong>Gespreksaudio wordt NIET via Lovable Cloud of Azure verstuurd.</strong> De WebRTC-verbinding loopt rechtstreeks
                      tussen de browser en de SIP-telefooncentrale van de praktijk. Lovable Cloud levert uitsluitend de SIP-configuratie (geen credentials van eindgesprekken).
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* 8. Overige stromen */}
              <AccordionItem value="other">
                <AccordionTrigger className="text-sm font-medium">
                  📊 Overige datastromen
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <p><strong>Gebruikersauthenticatie (admin):</strong> Via Lovable Cloud (email/wachtwoord). Geen patiëntdata.</p>
                    <p><strong>Praktijk- en gebruikersinstellingen:</strong> Opgeslagen in Lovable Cloud database. Bevat configuratie (rollen, voorkeuren), geen patiëntdata.</p>
                    <p><strong>Toegangsvalidatie:</strong> Praktijktoken + user_id worden gevalideerd via edge function. Bevat geen patiëntdata.</p>
                    <p><strong>Gebruiksregistratie (ai_usage_logs):</strong> Logt het aantal seconden spraakopname en AI-kosten per sessie.
                      Bevat <strong>geen</strong> transcript, audio of patiëntgegevens — alleen metagegevens (duur, type, kosten).</p>
                    <p><strong>SOEP-retour naar MicroHIS:</strong> Via WebBridge (lokale postMessage) of HTTP POST naar localhost.
                      Gaat <strong>niet</strong> over het internet.</p>
                  </div>
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </CardContent>
        </Card>

        {/* Samenvattingstabel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Samenvattingstabel — Alle datastromen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-2 font-medium">Van</th>
                    <th className="text-left p-2 font-medium">Naar</th>
                    <th className="text-left p-2 font-medium">Data</th>
                    <th className="text-left p-2 font-medium">Via</th>
                    <th className="text-left p-2 font-medium">Persoons­gegevens?</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <DataFlowRow from="Browser" to="Azure Speech" data="Live audio consult" via="Direct (WebSocket)" personal={true} />
                  <DataFlowRow from="Azure Speech" to="Browser" data="Transcript + sprekers" via="Direct (WebSocket)" personal={true} />
                  <DataFlowRow from="Browser" to="Edge Function" data="Auth token request" via="Lovable Cloud" personal={false} />
                  <DataFlowRow from="Browser" to="Azure AI Foundry" data="Transcript + prompt (SOEP)" via="Direct (client-side)" personal={true} />
                  <DataFlowRow from="Azure AI Foundry" to="Browser" data="SOEP-verslag (JSON)" via="Direct (client-side)" personal={true} />
                  <DataFlowRow from="Browser" to="Azure AI Foundry" data="Zin voor vertaling (bi-directioneel)" via="Direct (client-side)" personal={true} />
                  <DataFlowRow from="Browser" to="Azure Speech (TTS)" data="Tekst voor voorlezen" via="Direct (Speech SDK)" personal={true} />
                  <DataFlowRow from="Azure Speech (TTS)" to="Browser" data="Audio (MP3)" via="Direct (Speech SDK)" personal={false} />
                  <DataFlowRow from="Browser" to="Azure AI Foundry" data="Evaluatietekst (ICPC)" via="Direct (client-side)" personal={false} />
                  <DataFlowRow from="Browser" to="Edge Function" data="ICPC-codes validatie" via="Lovable Cloud" personal={false} />
                  <DataFlowRow from="Browser" to="Edge Function" data="E-mailadres + verificatie" via="Lovable Cloud" personal={true} />
                  <DataFlowRow from="Edge Function" to="Microsoft Graph" data="Verificatie-e-mail" via="Lovable Cloud" personal={true} />
                  <DataFlowRow from="Browser" to="MicroHIS" data="SOEP payload" via="Lokaal (WebBridge/HTTP)" personal={true} />
                  <DataFlowRow from="Browser" to="Edge Function" data="Gebruikslog (seconden/kosten)" via="Lovable Cloud" personal={false} />
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Verwerkersovereenkomsten */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-primary" />
              Benodigde verwerkersovereenkomsten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-2 font-medium">Partij</th>
                    <th className="text-left p-2 font-medium">Verwerking</th>
                    <th className="text-left p-2 font-medium">Scenario</th>
                    <th className="text-left p-2 font-medium">Noodzaak</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t">
                    <td className="p-2 font-medium">Microsoft Azure</td>
                    <td className="p-2">Speech-to-Text, TTS, AI Foundry (SOEP, vertaling, ICPC)</td>
                    <td className="p-2">Altijd — alle AI-verwerking loopt via Azure</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Verplicht</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">Lovable (Stockholm)</td>
                    <td className="p-2">Hosting web-app, database (configuratie, statistieken), authenticatietokens, ICPC-validatie, e-mail</td>
                    <td className="p-2">Altijd — maar geen medische data</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Verplicht</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">Microsoft 365 (Graph API)</td>
                    <td className="p-2">Versturen verificatie-e-mails bij onboarding</td>
                    <td className="p-2">Eenmalig bij registratie nieuwe gebruiker</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Beperkt</Badge> — alleen e-mailadres</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                <p className="text-xs text-green-800 dark:text-green-300">
                  ✅ <strong>Alle AI-verwerking loopt via Azure.</strong> Transcripten, vertalingen en ICPC-suggesties
                  gaan rechtstreeks van de browser naar uw eigen Azure-tenant. Lovable Cloud verwerkt
                  <strong> geen</strong> medische of persoonsgegevens — alleen configuratie, tokens en statistieken.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AVG aanbevelingen */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Shield className="h-4 w-4 text-primary" />
              AVG-aanbevelingen & mitigaties
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-muted-foreground">
            <div className="space-y-3">
              <div className="border-l-2 border-green-500 pl-3">
                <p className="font-medium text-foreground">✅ Audio gaat direct naar Azure</p>
                <p>De spraakherkenning verloopt direct van de browser naar Azure Speech Services.
                  Lovable Cloud ontvangt geen audio. De verwerkersovereenkomst met Microsoft dekt dit.</p>
              </div>
              <div className="border-l-2 border-green-500 pl-3">
                <p className="font-medium text-foreground">✅ SOEP-generatie gaat direct naar eigen Azure-tenant</p>
                <p>Het transcript wordt rechtstreeks vanuit de browser naar uw eigen Azure AI Foundry-tenant gestuurd.
                  Lovable Cloud is hier niet bij betrokken. De verwerking valt onder uw eigen DPA met Microsoft.</p>
              </div>
              <div className="border-l-2 border-green-500 pl-3">
                <p className="font-medium text-foreground">✅ Vertaling gaat direct naar eigen Azure-tenant</p>
                <p>Berichten worden bi-directioneel vertaald (zorgverlener ↔ patiënt) rechtstreeks vanuit de browser naar Azure AI Foundry.
                  Er passeert geen medische tekst via Lovable Cloud.</p>
              </div>
              <div className="border-l-2 border-green-500 pl-3">
                <p className="font-medium text-foreground">✅ ICPC-suggesties gaan direct naar eigen Azure-tenant</p>
                <p>De evaluatietekst wordt rechtstreeks vanuit de browser naar Azure AI Foundry gestuurd.
                  Alleen de gesuggereerde codes (geen evaluatietekst) worden via een edge function gevalideerd tegen de ICPC-database.</p>
              </div>
              <div className="border-l-2 border-green-500 pl-3">
                <p className="font-medium text-foreground">✅ TTS gaat direct naar Azure</p>
                <p>Text-to-Speech verloopt direct van de browser naar Azure Speech Services via de Speech SDK.
                  Lovable Cloud ontvangt geen tekst of audio — alleen het authenticatietoken.</p>
              </div>
              <div className="border-l-2 border-green-500 pl-3">
                <p className="font-medium text-foreground">✅ Telefonie (SIP) gaat direct naar de eigen PBX</p>
                <p>SIP-registratie, signalering en gesprekaudio (WebRTC/SRTP) lopen rechtstreeks tussen de browser
                  en de telefooncentrale van de praktijk. Lovable Cloud en Azure ontvangen geen gespreksaudio.
                  De SIP-configuratie (host, extensie, wachtwoord) wordt opgeslagen in Lovable Cloud zodat de softphone kan registreren.</p>
              </div>
              <div className="border-l-2 border-purple-500 pl-3">
                <p className="font-medium text-foreground">🔐 Geen permanente opslag van medische gegevens</p>
                <p>Lovable Cloud slaat geen transcripten, audio of SOEP-verslagen op.
                  De database bevat uitsluitend configuratie en gebruiksstatistieken (duur, kosten).
                  Het SOEP-verslag wordt alleen lokaal in het browsergeheugen bewaard
                  totdat het naar MicroHIS wordt verstuurd.</p>
              </div>
              <div className="border-l-2 border-blue-500 pl-3">
                <p className="font-medium text-foreground">📋 Azure API key client-side</p>
                <p>De Azure AI Foundry API key is zichtbaar in de browser (noodzakelijk voor directe aanroep).
                  Dit is acceptabel omdat de key beperkt is tot AI-completions op de eigen tenant.
                  Overweeg IP-restricties in Azure voor extra beveiliging.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technische details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Technische details — Edge Functions & Client-side calls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-2 font-medium">Functie / Aanroep</th>
                    <th className="text-left p-2 font-medium">Type</th>
                    <th className="text-left p-2 font-medium">Doel</th>
                    <th className="text-left p-2 font-medium">Externe dienst</th>
                    <th className="text-left p-2 font-medium">Bevat patiëntdata?</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t bg-green-50/30 dark:bg-green-950/10">
                    <td className="p-2 font-mono">Azure AI Foundry</td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">Client-side</Badge></td>
                    <td className="p-2">Transcript → SOEP verslag</td>
                    <td className="p-2">Azure AI (eigen tenant)</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Ja (transcript)</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">azure-speech-token</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">Auth token voor Speech SDK</td>
                    <td className="p-2">Azure STS</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                  </tr>
                  <tr className="border-t bg-green-50/30 dark:bg-green-950/10">
                    <td className="p-2 font-mono">Azure AI Foundry (Vertaling)</td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">Client-side</Badge></td>
                    <td className="p-2">Bi-directionele vertaling</td>
                    <td className="p-2">Azure AI (eigen tenant)</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Mogelijk</Badge></td>
                  </tr>
                  <tr className="border-t bg-green-50/30 dark:bg-green-950/10">
                    <td className="p-2 font-mono">Azure Speech TTS</td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">Client-side</Badge></td>
                    <td className="p-2">Tekst voorlezen (Speech SDK)</td>
                    <td className="p-2">Azure Speech Services (direct)</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Mogelijk</Badge></td>
                  </tr>
                  <tr className="border-t bg-green-50/30 dark:bg-green-950/10">
                    <td className="p-2 font-mono">Azure AI Foundry (ICPC)</td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">Client-side</Badge></td>
                    <td className="p-2">Evaluatietekst → ICPC-codes</td>
                    <td className="p-2">Azure AI (eigen tenant)</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Indirect</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">suggest-icpc</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">ICPC-code validatie tegen database</td>
                    <td className="p-2">Database</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee (alleen codes)</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">send-verification-email</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">Verificatie-e-mail</td>
                    <td className="p-2">Microsoft Graph</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">E-mailadres</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">log-usage</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">Gebruiksregistratie</td>
                    <td className="p-2">Geen (database)</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">validate-access-token</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">Toegangscontrole</td>
                    <td className="p-2">Geen (database)</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">list-azure-deployments</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">Azure modellen ophalen</td>
                    <td className="p-2">Azure Management API</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">get-usage</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">Gebruiksoverzicht per gebruiker</td>
                    <td className="p-2">Geen (database)</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">get-practice-usage</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">Kostenoverzicht per praktijk (admin)</td>
                    <td className="p-2">Geen (database)</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">update-practice-user</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Edge Function</Badge></td>
                    <td className="p-2">Gebruikersinstellingen bijwerken</td>
                    <td className="p-2">Geen (database)</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                  </tr>
                  <tr className="border-t bg-green-50/30 dark:bg-green-950/10">
                    <td className="p-2 font-mono">SIP softphone (JsSIP)</td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">Client-side</Badge></td>
                    <td className="p-2">Bellen via WSS + WebRTC (SRTP)</td>
                    <td className="p-2">SIP-PBX van de praktijk (direct)</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Ja (gesprekaudio)</Badge></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Wat wordt wél opgeslagen */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Wat wordt wél opgeslagen in de database?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-2 font-medium">Gegevens</th>
                    <th className="text-left p-2 font-medium">Tabel</th>
                    <th className="text-left p-2 font-medium">Persoonsgegevens?</th>
                    <th className="text-left p-2 font-medium">Doel</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t">
                    <td className="p-2">Praktijknaam, AGB-code, instellingen</td>
                    <td className="p-2 font-mono">practices</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                    <td className="p-2">Configuratie</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Gebruikersnaam, e-mail, rol, voorkeuren</td>
                    <td className="p-2 font-mono">practice_users</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Ja</Badge></td>
                    <td className="p-2">Gebruikersbeheer</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Duur opname, type, kosten, AI-model (geen inhoud)</td>
                    <td className="p-2 font-mono">ai_usage_logs</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                    <td className="p-2">Kostenregistratie</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">AI-provider, model, Azure credentials</td>
                    <td className="p-2 font-mono">app_settings</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                    <td className="p-2">Applicatie-configuratie</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">ICPC-codes en beschrijvingen</td>
                    <td className="p-2 font-mono">icpc_codes</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                    <td className="p-2">Referentiedata</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Tariefperiodes, marges, verkoopprijzen</td>
                    <td className="p-2 font-mono">billing_settings</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                    <td className="p-2">Facturatie-instellingen</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">SIP-configuratie (host, WSS-pad, STUN, beluitprefix)</td>
                    <td className="p-2 font-mono">practices</td>
                    <td className="p-2"><Badge variant="secondary" className="text-[10px]">Nee</Badge></td>
                    <td className="p-2">Softphone-verbinding met PBX van de praktijk</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2">Persoonlijke SIP-extensie + wachtwoord</td>
                    <td className="p-2 font-mono">practice_users</td>
                    <td className="p-2"><Badge variant="destructive" className="text-[10px]">Ja</Badge></td>
                    <td className="p-2">Authenticatie van de gebruiker bij de eigen telefooncentrale</td>
                  </tr>
                  <tr className="border-t bg-green-50/30 dark:bg-green-950/10">
                    <td className="p-2 font-medium" colSpan={4}>
                      ❌ Transcripten, audio-opnames en SOEP-verslagen worden <strong>NIET</strong> opgeslagen in de database.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Verwerkersovereenkomsten sectie */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Benodigde Verwerkersovereenkomsten (DPA's)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Voor het AVG-conform inzetten van deze applicatie zijn verwerkersovereenkomsten nodig met de volgende partijen.
              De applicatie kent twee categorieën verwerkers met elk een duidelijk afgebakende rol.
            </p>

            <div className="space-y-4">
              {/* Azure */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold">1. Microsoft Azure (AI & Spraakverwerking)</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b">
                        <td className="p-2 font-medium w-44">Juridische entiteit</td>
                        <td className="p-2">Microsoft Ireland Operations Limited (EU-regio)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Rol</td>
                        <td className="p-2">Verwerker (sub-verwerker via Azure AI Foundry)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Verwerkte data</td>
                        <td className="p-2">
                          <ul className="list-disc list-inside space-y-1">
                            <li>Audio-opnames (spraak-naar-tekst via Azure Speech Services)</li>
                            <li>Transcripten (SOEP-generatie via Azure OpenAI / GPT-modellen)</li>
                            <li>SOEP-evaluatietekst (ICPC-code suggesties)</li>
                            <li>Vrije tekst (bi-directionele vertaling tussen zorgverlener- en patiënttaal)</li>
                            <li>SOEP-tekst (Text-to-Speech voorlezen)</li>
                          </ul>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Dataverwerking</td>
                        <td className="p-2">
                          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-[10px]">
                            Client-side → Azure direct
                          </Badge>
                          <span className="ml-2 text-xs text-muted-foreground">Geen tussenliggende servers</span>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Opslag</td>
                        <td className="p-2">Geen permanente opslag — data wordt alleen verwerkt (transient)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Regio</td>
                        <td className="p-2">Configureerbaar per praktijk (aanbevolen: West Europe / North Europe)</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">DPA verkrijgen</td>
                        <td className="p-2">
                          Automatisch onderdeel van het Microsoft Online Services DPA. Beschikbaar via{' '}
                          <a href="https://www.microsoft.com/licensing/docs/view/Microsoft-Products-and-Services-Data-Protection-Addendum-DPA" 
                             target="_blank" rel="noopener noreferrer"
                             className="text-primary underline">
                            Microsoft DPA pagina
                          </a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Lovable */}
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-purple-600" />
                  <h4 className="font-semibold">2. Lovable Technologies (Hosting & Database)</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b">
                        <td className="p-2 font-medium w-44">Juridische entiteit</td>
                        <td className="p-2">
                          Lovable (voorheen GPT Engineer), Stockholm, Zweden
                          <br />
                          <span className="text-xs text-muted-foreground">⚠️ Verifieer exacte handelsnaam via hun website/KvK-equivalent</span>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Rol</td>
                        <td className="p-2">Verwerker (hosting van webapplicatie en database)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Verwerkte data</td>
                        <td className="p-2">
                          <ul className="list-disc list-inside space-y-1">
                            <li>Gebruikersaccounts en profielen (praktijkgebruikers)</li>
                            <li>Praktijkconfiguratie en instellingen</li>
                            <li>Verbruiksregistratie (ai_usage_logs — alleen duur/type, geen inhoud)</li>
                            <li>ICPC-referentiecodes (geen persoonsgegevens)</li>
                            <li>Facturatie-instellingen</li>
                          </ul>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Niet verwerkt</td>
                        <td className="p-2">
                          <Badge variant="destructive" className="text-[10px]">Geen medische data</Badge>
                          <span className="ml-2 text-xs">Geen audio, transcripten, SOEP-verslagen of patiëntgegevens</span>
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">Infrastructuur</td>
                        <td className="p-2">Lovable Cloud (gebaseerd op Supabase — EU-hosting)</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">DPA verkrijgen</td>
                        <td className="p-2">
                          Op te vragen bij Lovable via{' '}
                          <a href="https://lovable.dev" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                            lovable.dev
                          </a>
                          {' '}of per e-mail bij hun juridische afdeling
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Samenvatting */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Checklist voor uw Functionaris Gegevensbescherming (FG)
                </h4>
                <ol className="list-decimal list-inside text-sm space-y-1 text-muted-foreground">
                  <li>Microsoft DPA is automatisch van kracht bij gebruik Azure-diensten — verifieer dat uw Azure-abonnement onder de juiste entiteit valt</li>
                  <li>Vraag een verwerkersovereenkomst op bij Lovable Technologies voor de hosting/database-component</li>
                  <li>Configureer Azure-regio op EU (West Europe of North Europe) voor dataresidentie</li>
                  <li>Documenteer in uw verwerkingsregister welke categorieën persoonsgegevens via welke verwerker lopen</li>
                  <li>Voer een DPIA (Data Protection Impact Assessment) uit vanwege de verwerking van gezondheidsgegevens (bijzondere persoonsgegevens)</li>
                  <li>Controleer of uw beroepsaansprakelijkheidsverzekering digitale gegevensverwerking dekt</li>
                  <li>SIP-telefonie: gespreksaudio loopt rechtstreeks naar uw eigen PBX. Stem zo nodig een aparte verwerkersovereenkomst af met uw telecom-/PBX-leverancier indien deze geen onderdeel is van uw eigen praktijkinfrastructuur</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
